import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../../../lib/utils';
import { SearchIcon, XIcon } from '../../../icons';
import { Input, type InputProps } from '../input';

type ForwardedInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'size' | 'className'
>;

export interface SearchInputProps extends ForwardedInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called when the clear (X) button is clicked. Defaults to `onChange('')`. */
  onClear?: () => void;
  /** Forwarded to the underlying `Input` variant. */
  variant?: InputProps['variant'];
  /** Class names applied to the outer wrapper (controls width/visibility). */
  className?: string;
  'data-testid'?: string;
}

/**
 * Text input with a right-aligned magnifier that flips into a clear (X)
 * button when the field has content. Composed from `Input` + icons.
 *
 * Test ids — when `data-testid="x"` is passed, the input receives `x`
 * and the clear button receives `x-clear`.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onChange, onClear, variant, className, 'data-testid': testId, ...rest }, ref) => {
    const isActive = value.length > 0;

    const handleClear = () => {
      if (onClear) onClear();
      else onChange('');
    };

    return (
      <div className={cn('relative', className)}>
        <Input
          ref={ref}
          type="text"
          variant={variant}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="pr-7"
          data-testid={testId}
          {...rest}
        />
        {isActive ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Limpar busca"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <XIcon className="size-4" />
          </button>
        ) : (
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
        )}
      </div>
    );
  }
);
SearchInput.displayName = 'SearchInput';
